import { DataProxy } from 'apollo-cache'
import { useToast } from 'containers/ToastManager'
import { ShareInfoFragment, ShareListItemFragment } from 'gqlTypes'
import { readShareListItemFromCache } from '../utils'
import { useShareSubscriptions } from './useShareSubscriptions'
import React, { ReactNode } from 'react'
import Link from 'components/Link'

import { getCanAdminister } from 'modules/shares/utils'
import { useShareRedirects } from 'modules/shares/routes/useShareRedirects'
import { routes } from 'constants/routes'
import { Variant } from 'components/Toasts/Toast'

const permanentlyDeletedToast = (name: string) => ({
  message: `"${name}" has been permanently deleted`,
  variant: 'negative' as const,
})

export const useShareSubscriptionsWithUi = () => {
  const { isUserViewing, redirectToDocumentsView } = useShareRedirects()
  const { showToast } = useToast()

  const redirectIfUserIsLookingAtShare = (
    cache: DataProxy,
    shareIdentifier: string,
    toast:
      | ((
          share: ShareInfoFragment | ShareListItemFragment
        ) => { message: ReactNode; variant: Variant })
      | undefined
  ) => {
    if (!isUserViewing.share(shareIdentifier)) {
      return
    }
    const share = readShareListItemFromCache({ cache, id: shareIdentifier })
    if (!share) return

    redirectToDocumentsView({
      workspaceId: share.workspace.identifier,
      toast: share ? toast?.(share) : undefined,
    })
  }

  useShareSubscriptions({
    onEmptiedWorkspaceTrash: ({ client, workspaceIdentifier }) => {
      const isLookingAtShare = isUserViewing.anyShare()
      if (!isLookingAtShare) return

      const { shareID } = isLookingAtShare
      const share = readShareListItemFromCache({ cache: client, id: shareID })
      if (!share) return

      if (
        share.workspace?.identifier === workspaceIdentifier &&
        share.deletedAt
      ) {
        redirectToDocumentsView({
          workspaceId: workspaceIdentifier,
          toast: permanentlyDeletedToast(share.name),
        })
      }
    },

    onDeletedSharePermanently: ({ client, shareIdentifier }) =>
      redirectIfUserIsLookingAtShare(client, shareIdentifier, share =>
        permanentlyDeletedToast(share.name)
      ),

    onShareDeleted: ({ client, share }) =>
      redirectIfUserIsLookingAtShare(client, share.identifier, share => {
        const link = routes.WORKSPACE_TRASH.create({
          workspaceId: share.workspace.identifier,
        })

        return {
          variant: 'negative',
          message: getCanAdminister(share) ? (
            <>
              &quot;
              {share.name}
              &quot; has been moved to <Link to={link}>Trash</Link>
            </>
          ) : (
            `"${share.name}" has been deleted`
          ),
        }
      }),

    onShareRestored: ({ client, share: { identifier } }) => {
      if (isUserViewing.share(identifier)) {
        const share = readShareListItemFromCache({
          cache: client,
          id: identifier,
        })
        share && showToast(`"${share.name}" has been restored`)
      }
    },
  })
}
